/**
 * 语法练习生成器
 * ------------------------------------------------------------
 * 语法分类不是程序臆造的：分类来自「导入内容」里的 grammar[].category，
 * 生成器只按分类过滤 + 从该单元已有题目里抽题，必要时用语法点自动造句式判断题。
 *
 * 支持三种出题方式：
 *   1) 教材/导入的语法题（questions 里 skill=G 的题）——原题直接用
 *   2) 由语法点 examples 自动生成的「判断句子是否属于该规则」
 *   3) 以上混合
 */
import { registerGenerator } from './registry.js';
import { classifyGrammar, groupGrammarByPoint } from '../grammarPoints.js';
import { makeQuestion } from '../../core/models.js';
import { shuffle, sample, pick } from '../../utils/index.js';

/** 从内容里收集语法分类（去重、保序） */
export function grammarCategoriesOf(content) {
  return groupGrammarByPoint(content).map(g => g.name);
}

/**
 * 判断某道语法题 / 某个语法点是否属于选中的知识点。
 * category 可以是：
 *   ''             → 全部（混合练习）
 *   知识点 id      → be / have / can / there-be ...（由 grammarPoints.js 归一）
 *   教材原文分类    → 兼容旧数据与"无法归一"的分类
 */
function belongs(textOfItem, category) {
  if (!category) return true;
  const pointId = classifyGrammar(textOfItem);
  if (pointId && pointId === category) return true;
  return String(textOfItem || '').includes(category);
}

function fromBank(content, category, count) {
  const bank = (content.questions || []).filter(q => {
    if (q.skill !== 'G' || q.type === 'order') return false;
    if (!category) return true;
    return belongs(`${q.grammarCategory || ''} ${q.prompt || ''}`, category);
  });
  return sample(bank, count);
}

function fromGrammarPoints(content, category, count) {
  const points = (content.grammar || []).filter(g =>
    !category || belongs(`${g.category || ''} ${g.rule || ''}`, category));
  if (!points.length) return [];
  const out = [];
  for (let i = 0; i < count; i++) {
    const g = points[i % points.length];
    if (g.examples?.length) {
      const ex = pick(g.examples);
      const ok = Math.random() < 0.5;
      const otherPoint = pick(points.filter(p => p.id !== g.id) || points);
      const shown = ok ? ex : (otherPoint?.examples?.[0] || ex);
      out.push(makeQuestion({
        textbookId: g.textbookId,
        unitId: g.unitId,
        refType: 'grammar',
        refId: g.id,
        grammarCategory: g.category,
        type: 'judge',
        skill: 'G',
        level: 1,
        prompt: `下面这个句子符合「${g.category}」的这个规则吗？\n规则：${g.rule}\n句子：${shown}`,
        options: ['符合', '不符合'],
        answer: [ok ? '符合' : '不符合'],
        explain: `${ok ? '符合' : '不符合'}。${g.rule}\n例句：${ex}`,
        hint: '',
        origin: 'generated',
        status: 'confirmed',
      }));
    }
  }
  return out;
}

function build({ content, category = '', count = 10, mode = 'mixed' } = {}) {
  const bankCount = Math.ceil(count * 0.7);
  let list = [];
  if (mode !== 'grammar-only') list = fromBank(content, category, bankCount);
  if (mode !== 'bank-only' && list.length < count) {
    list = list.concat(fromGrammarPoints(content, category, count - list.length));
  }
  if (list.length < count) list = list.concat(fromBank(content, category, count - list.length));
  return shuffle(list).slice(0, count);
}

export const grammarGenerator = registerGenerator({
  id: 'grammar',
  module: 'grammar',
  title: '语法专项',
  levels: [],
  build,
});

export default grammarGenerator;
